'use client';

/**
 * CookieConsentBanner — bottom-of-viewport slim banner.
 *
 * DR stance: in the US, cookie banners aren't usually required, but Microsoft
 * Clarity's policy + any EU exposure makes one prudent. Rendering is gated by
 * an env flag passed from the root layout:
 *
 *   'never'    → never render (only safe for single-jurisdiction deployments
 *                that have no EU exposure — document this choice explicitly)
 *   'eu_only'  → render only when CF-IPCountry suggests EU/EEA/UK
 *   'always'   → render on every first visit (default, safest)
 *
 * Preference is persisted in the `cookie_consent` cookie (JSON) for 1 year.
 * The MicrosoftClarity component reads the same cookie and only fires
 * Clarity.init when `analytics` is true.
 *
 * Two buttons by default: Accept (all optional on) · Manage (inline
 * category toggles). Essential cookies are always-on / disabled.
 */

import { useEffect, useState } from 'react';

export interface CookieConsentState {
  essential: true; // always on
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: 'v1';
}

export const COOKIE_NAME = 'cookie_consent';
const CONSENT_VERSION: CookieConsentState['version'] = 'v1';

function readConsentCookie(): CookieConsentState | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]!));
    if (parsed && typeof parsed === 'object' && parsed.version === CONSENT_VERSION) {
      return parsed as CookieConsentState;
    }
  } catch {
    /* fall through */
  }
  return null;
}

function writeConsentCookie(state: CookieConsentState) {
  if (typeof document === 'undefined') return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))};` +
    ` path=/; max-age=${oneYear}; samesite=lax`;
}

export default function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    // `NEXT_PUBLIC_SHOW_COOKIE_BANNER` gate:
    //   'never'    → banner never renders (only safe for single-jurisdiction
    //                deployments with no EU exposure — document the choice).
    //   'eu_only'  → TODO: requires server-side CF-IPCountry detection; for
    //                now behaves like 'always' until we add a server wrapper.
    //   anything else (including 'always', unset, undefined) → show on first
    //   visit until the user makes a choice.
    const mode = process.env.NEXT_PUBLIC_SHOW_COOKIE_BANNER ?? 'always';
    if (mode === 'never') return;
    const existing = readConsentCookie();
    if (!existing) setShow(true);
  }, []);

  if (!show) return null;

  function acceptAll() {
    writeConsentCookie({
      essential: true,
      analytics: true,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setShow(false);
    // MicrosoftClarity listens on mount; a soft reload on next nav picks up
    // the new consent. No reload here — avoids a jarring full refresh.
  }

  function saveCustom() {
    writeConsentCookie({
      essential: true,
      analytics,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setShow(false);
  }

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white shadow-lg"
    >
      <div className="mx-auto max-w-6xl px-6 py-4">
        {!manageOpen && (
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <p className="text-xs text-slate-600 md:flex-1 leading-relaxed">
              We use essential cookies, plus optional analytics to improve the site.
              {' '}
              <a href="/cookie-policy" className="underline hover:text-slate-900">Cookie Policy</a>
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Manage
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white bg-[#0f2534] rounded-lg hover:bg-[#0a1b26]"
              >
                Accept
              </button>
            </div>
          </div>
        )}

        {manageOpen && (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Choose which optional categories to allow. Essential cookies are
              required for sign-in and basic site functionality.
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span className="font-medium">Essential</span>
                <span className="text-slate-500">— required, always on</span>
              </label>
              <label className="flex items-center gap-3 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#ff9100] focus:ring-[#ff9100] cursor-pointer"
                />
                <span className="font-medium">Analytics</span>
                <span className="text-slate-500">— Microsoft Clarity on marketing pages only</span>
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setManageOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white bg-[#0f2534] rounded-lg hover:bg-[#0a1b26]"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
